import { isButtonField } from '@/features/records/utils/buttonField'
import { canViewField, viewableFields } from '@/features/records/utils/fieldAccess'
import { useForm } from '@/hooks/useForm'
import { useTags } from '@/hooks/useTags'
import type { AutomationTriggerFilter } from '@/types/api/automation'
import { cn } from '@/utils/cn'

interface ChipPickerProps {
  /** What the empty selection means, e.g. "any tag". */
  intro: string
  options: { value: string; label: string }[]
  selected: string[]
  onToggle: (value: string) => void
  emptyText: string
}

function ChipPicker({ intro, options, selected, onToggle, emptyText }: ChipPickerProps) {
  return (
    <div className="space-y-2 rounded-xl border border-neutral-200 bg-neutral-50/60 p-3">
      <p className="text-xs text-neutral-500">{intro}</p>
      {options.length === 0 ? (
        <p className="text-xs italic text-neutral-400">{emptyText}</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              aria-pressed={selected.includes(option.value)}
              onClick={() => onToggle(option.value)}
              className={cn(
                'rounded-full border px-2.5 py-1 text-xs transition-colors',
                selected.includes(option.value)
                  ? 'border-primary-500 bg-primary-50 font-semibold text-primary-800'
                  : 'border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-100'
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

interface PickerProps {
  recordGroupId: string
  value: AutomationTriggerFilter
  onChange: (value: AutomationTriggerFilter) => void
}

const toggled = (current: string[], item: string) =>
  current.includes(item) ? current.filter((v) => v !== item) : [...current, item]

/** Which tags fire TAG_ADDED / TAG_REMOVED. Nothing selected means any tag of the group. */
export function TagTriggerFilterPicker({ recordGroupId, value, onChange }: PickerProps) {
  const { tags } = useTags(recordGroupId || undefined, 'RECORD')
  return (
    <ChipPicker
      intro="Only run for these tags (leave empty for any tag):"
      emptyText={recordGroupId ? 'This group has no record tags yet.' : 'Choose a record group first.'}
      options={tags.filter((t) => !t.archived).map((t) => ({ value: t.id, label: t.label }))}
      selected={value.tagIds ?? []}
      onToggle={(id) => onChange({ ...value, tagIds: toggled(value.tagIds ?? [], id) })}
    />
  )
}

/** Which field changes fire UPDATE. Nothing selected means a change to any field. */
export function FieldTriggerFilterPicker({ recordGroupId, value, onChange }: PickerProps) {
  const { form } = useForm(recordGroupId, { enabled: !!recordGroupId })
  const fields = viewableFields(form?.fields ?? []).sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
  return (
    <ChipPicker
      intro="Only run when one of these fields changes (leave empty for any field):"
      emptyText={recordGroupId ? 'This group has no form fields yet.' : 'Choose a record group first.'}
      options={fields.map((f) => ({ value: f.key, label: f.label }))}
      selected={value.fieldKeys ?? []}
      onToggle={(key) => onChange({ ...value, fieldKeys: toggled(value.fieldKeys ?? [], key) })}
    />
  )
}

/** Which buttons fire BUTTON_CLICKED. At least one is needed: a button automation has to say which. */
export function ButtonTriggerFilterPicker({ recordGroupId, value, onChange }: PickerProps) {
  const { form } = useForm(recordGroupId, { enabled: !!recordGroupId })
  const buttons = (form?.fields ?? [])
    .filter((f) => isButtonField(f) && canViewField(f))
    .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
  return (
    <ChipPicker
      intro="Run when one of these buttons is clicked:"
      emptyText={recordGroupId ? 'This group has no buttons yet. Add a Button field in the form designer.' : 'Choose a record group first.'}
      options={buttons.map((f) => ({ value: f.key, label: f.label }))}
      selected={value.fieldKeys ?? []}
      onToggle={(key) => onChange({ ...value, fieldKeys: toggled(value.fieldKeys ?? [], key) })}
    />
  )
}

/** Variables a template can use: the group's field keys plus the fixed ones every trigger provides. */
export function AvailableVariables({ recordGroupId }: { recordGroupId: string }) {
  const { form } = useForm(recordGroupId, { enabled: !!recordGroupId })
  const variables = [
    ...viewableFields(form?.fields ?? []).map((f) => f.key),
    'record.title',
    'actor.name',
    'tag.label',
  ]
  return (
    <p className="text-[11px] leading-relaxed text-neutral-500">
      Available variables:{' '}
      {variables.map((v, i) => (
        <span key={v}>
          <code className="rounded bg-neutral-100 px-1 py-px font-mono text-neutral-700">{`{{${v}}}`}</code>
          {i < variables.length - 1 ? ' ' : ''}
        </span>
      ))}
      <span className="block text-neutral-400">
        <code className="font-mono">tag.label</code> is filled only for tag triggers.
      </span>
    </p>
  )
}
